
export enum AsyncOperationState {
  PROGRESSING = 'progressing',
  COMPLETE = 'complete',
  ERROR = 'error',
  INITIATED = 'initiated',
  CANCELED = 'canceled',
}

// Only to be used to make the metering response type safe.
// These strings are built dynamically in the response from search when fetching metering data
// by appending the metering's itemType to the operation.
export enum CognitiveOperationTypeForImage {
  PERSON_DETECTION_IMAGE = 'personDetectionImage',
  LABEL_DETECTION_IMAGE = 'labelDetectionImage',
  TEXT_DETECTION_IMAGE = 'textDetectionImage',
}

export enum ReprocessActionType {
  TRANSCODING_STATE_PROGRESSING = 'transcodingStateProgressing',

  // This is for Kelda live ingest
  LIVE_ITEM_REINGEST = 'liveItemReIngest',

  REPROCESS_TS_SEGMENTS = 'reprocessTsSegments',
}


// TODO: AsyncOperationType was meant to only contain "general" wrapper jobs that are retryable.
// Now other jobs are mixed up here. We need to separate types.
export const generalAsyncOperationTypes = <const> [
  'transcoding', 'transcription', 'labelDetection',
  'personDetection', 'celebrityDetection', 'textDetection', 'sceneDetection',
  'archiveFileTransfer',
];

export type GeneralAsyncOperationType = typeof generalAsyncOperationTypes[number];


export enum ErrorType {
  GenericError = 'GenericError',
  InputError = 'InputError',
  MediaError = 'MediaError',
  LongMediaError = 'LongMediaError',
  EmptyMediaError = 'EmptyMediaError',
  RetryableError = 'RetryableError',
  LimitedRetryableError = 'LimitedRetryableError',
  MimirMaxJobsExceededError = 'MimirMaxJobsExceededError',
  TaskCreationError = 'TaskCreationError',
  TimeoutError = 'TimeoutError',
  TranscriptionJobCreationError = 'TranscriptionJobCreationError',
  TranscriptionJobStatusError = 'TranscriptionJobStatusError',
  TranscriptionFetchError = 'TranscriptionFetchError',
  TrintDurationLimitError = 'TrintDurationLimitError',
  TrintFileSizeLimitError = 'TrintFileSizeLimitError',
}

export enum UserOperationType {
  ARCHIVE_INITIATED = 'archiveInitiated',
  ARCHIVE_COMPLETED = 'archiveCompleted',
  UNARCHIVE = 'unarchive',
  TIMED_METADATA_UPDATE = 'timedMetadataUpdate',
  METADATA_UPDATE = 'metadataUpdate',
  TRANSCRIPT_UPDATE = 'transcriptUpdate',
  TRANSLATION_UPDATE = 'translationUpdate',
  GEO_MARKER_UPDATE = 'geoMarkerUpdate',
  SEQUENCE_DETAILS_UPDATE = 'sequenceDetailsUpdate',
  DETECTED_PERSON_UPDATE = 'detectedPersonUpdate',
  DETECTED_CELEBRITIES_UPDATE = 'detectedCelebritiesUpdate',
  DETECTED_LABEL_UPDATE = 'detectedLabelUpdate',
  FOLDER_PARENT_REMOVE = 'folderParentRemove',
  FOLDER_PARENT_ADD = 'folderParentAdd',
  DELETE = 'delete',
  UNDELETE = 'undelete',
  CREATE = 'create',
  VERSION = 'version',
  MERGE = 'merge',
  INGEST = 'ingest',
  HIGHRES_CREATED = 'highresCreated',
  EDIT_PROXY_CREATED = 'editProxyCreated',
  WEB_PROXY_CREATED = 'webProxyCreated',
  MERGE_REQUEST_CREATED = 'mergeRequestCreated',
  PLACEHOLDER_CREATED_BY_METADATA_COPY = 'placeholderCreatedByMetadataCopy',
  HIGHRES_DELETE_SCHEDULED = 'highresDeleteScheduled',
  HIGHRES_DELETE_COMPLETED = 'highresDeleteCompleted',
  HIGHRES_DELETE_FAILED = 'highresDeleteFailed',
  HIGHRES_DELETE_CANCELLED = 'highresDeleteCancelled',
  HIGHRES_DELETE_STATE_UPDATE = 'highresDeleteStateUpdate',
  HIGHRES_DELETE_DATE_UPDATE = 'highresDeleteDateUpdate',
  VISIBILITY_CHANGE = 'visibilityChange',
  HIGHRES_LOCATION_MIGRATED = 'highresLocationMigrated',
  WEB_PROXY_LOCATION_MIGRATED = 'webProxyLocationMigrated',
  EDIT_PROXY_LOCATION_MIGRATED = 'editProxyLocationMigrated',
  THUMBNAIL_STREAM_LOCATION_MIGRATED = 'thumbnailStreamLocationMigrated',
  DELETED_AS_PART_OF_FOLDER_DELETION = 'deletedAsPartOfFolderDeletion',
}



export interface AsyncOperationInputFile {
  objectPath: string;
  bucketName: string;
}

/**
 * Parameters controlling the person detection analytics operation.
 */
export interface PersonDetectionParams {
  // If true we should detect unrecognized faces in the image
  // or video and create person items for these.
  createUnknownPersons?: boolean
}


export interface ArchiveFileTransferParams {
  mediaOnly?: boolean;
  archiveFolderPath?: string;
  isArchiveFolderPathFromPayload?: boolean;
  skipIsArchiveCheck?: boolean;
  generatedDataOnly?: boolean;
  doNotAugmentArchiveFolderPath?: boolean;
}

// All canceled job field names - typed so it becomes more concise to use in code
export type CanceledStateFieldName =
  | 'transcodingJobCanceled'
  | 'transcriptionJobCanceled'
  | 'personDetectionJobCanceled'
  | 'celebrityDetectionJobCanceled'
  | 'labelDetectionJobCanceled'
  | 'textDetectionJobCanceled';

